import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { View, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery } from '@tanstack/react-query';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import api from '../../../../utils/api';
import useDebouncedValue from '@/hooks/useDebouncedValue';


export type TicketFilters = {
  statuses: string[];
  area: string;        // 'all' | 'unassigned' | uuid
  category: string;    // 'all' | 'none' | uuid
  priority: string;    // 'all' | 'high'
  origin: string;      // 'all' | resident | security | admin
  street: string;      // '' = todas
  unit: { uuid: string; name: string } | null;
  from: string;        // YYYY-MM-DD
  to: string;
  search: string;
};

export const EMPTY_FILTERS: TicketFilters = {
  statuses: [],
  area: 'all',
  category: 'all',
  priority: 'all',
  origin: 'all',
  street: '',
  unit: null,
  from: '',
  to: '',
  search: '',
};

/** Filtros → query params de /tickets, /tickets/counts y /tickets/report. */
export function filtersToParams(f: TicketFilters, search: string = f.search) {
  const params: Record<string, any> = {};
  if (f.statuses.length) params.status = f.statuses.join(',');
  if (f.area !== 'all') params.area_uuid = f.area;
  if (f.category !== 'all') params.category_uuid = f.category;
  if (f.priority === 'high') params.high_priority = 1;
  if (f.origin !== 'all') params.origin = f.origin;
  if (f.street) params.street = f.street;
  if (f.unit) params.unit_uuid = f.unit.uuid;
  if (f.from) params.from = f.from;
  if (f.to) params.to = f.to;
  if (search) params.search = search;
  return params;
}

/** Cuántos filtros (sin contar búsqueda) se están aplicando. */
export function activeFiltersCount(f: TicketFilters) {
  return [
    f.statuses.length > 0, f.area !== 'all', f.category !== 'all', f.priority !== 'all',
    f.origin !== 'all', !!f.street, !!f.unit, !!f.from, !!f.to,
  ].filter(Boolean).length;
}

const STATUS_OPTIONS = [
  { value: 'nuevo', label: 'Nuevos' },
  { value: 'en_seguimiento', label: 'En seguimiento' },
  { value: 'atendida', label: 'Atendidos' },
];


/**
 * `canSeeCommunity`: calle y vivienda salen de endpoints que piden tickets.view
 * o tickets.manage; sin ellos esos filtros no se muestran.
 */
export default function TicketFiltersBar({ communityId, filters, onChange, areas, categories, canSeeCommunity, showStatus = true }: any) {
  const f: TicketFilters = filters;
  const set = (patch: Partial<TicketFilters>) => onChange({ ...f, ...patch });

  const streetsQuery = useQuery<string[]>({
    queryKey: ['ticket-filters.streets', communityId],
    queryFn: () => api.get(`/communities/${communityId}/ticket-filters/streets`).then(r => r.data),
    enabled: !!communityId && canSeeCommunity,
    staleTime: 5 * 60 * 1000,
  });
  const streets = streetsQuery.data ?? [];

  const toggleStatus = (status: string) => {
    set({
      statuses: f.statuses.includes(status)
        ? f.statuses.filter(s => s !== status)
        : [...f.statuses, status],
    });
  };

  const hasFilters = activeFiltersCount(f) > 0;

  return (
    <View style={tw`px-5 py-3 border-b border-gray-100 bg-white`}>
      <View style={tw`flex-row flex-wrap items-center gap-3`}>
        {showStatus && (
          <View style={tw`flex-row gap-2`}>
            <Pill label="Todos" active={f.statuses.length === 0} onPress={() => set({ statuses: [] })} />
            {STATUS_OPTIONS.map(opt => (
              <Pill
                key={opt.value}
                label={opt.label}
                active={f.statuses.includes(opt.value)}
                onPress={() => toggleStatus(opt.value)}
              />
            ))}
          </View>
        )}

        <View style={tw`flex-row items-center gap-2`}>
          <DateRangeInput value={f.from} max={f.to || undefined} placeholder="Desde" onChange={(from: string) => set({ from })} />
          <Text style={tw`text-xs text-gray-400`}>a</Text>
          <DateRangeInput value={f.to} min={f.from || undefined} placeholder="Hasta" onChange={(to: string) => set({ to })} />
        </View>

        <View style={tw`flex-1`} />

        {hasFilters && (
          <TouchableOpacity onPress={() => onChange({ ...EMPTY_FILTERS, search: f.search })}>
            <Text style={tw`text-xs text-secondary font-500`}>Limpiar filtros</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={tw`flex-row flex-wrap items-center gap-2 mt-3`}>
        <NativeSelect
          value={f.area}
          defaultValue={EMPTY_FILTERS.area}
          onChange={(area: string) => set({ area })}
          options={[
            { value: 'all', label: 'Todas las áreas' },
            { value: 'unassigned', label: 'Por asignar' },
            ...areas.map((a: any) => ({ value: a.uuid, label: a.name })),
          ]}
        />

        {categories.length > 0 && (
          <NativeSelect
            value={f.category}
            defaultValue={EMPTY_FILTERS.category}
            onChange={(category: string) => set({ category })}
            options={[
              { value: 'all', label: 'Todas las categorías' },
              { value: 'none', label: 'Sin categoría' },
              ...categories.map((c: any) => ({ value: c.uuid, label: c.name })),
            ]}
          />
        )}

        <NativeSelect
          value={f.origin}
          defaultValue={EMPTY_FILTERS.origin}
          onChange={(origin: string) => set({ origin })}
          options={[
            { value: 'all', label: 'Todo origen' },
            { value: 'resident', label: 'Residentes' },
            { value: 'security', label: 'Vigilancia' },
            { value: 'admin', label: 'Administración' },
          ]}
        />

        <NativeSelect
          value={f.priority}
          defaultValue={EMPTY_FILTERS.priority}
          onChange={(priority: string) => set({ priority })}
          options={[
            { value: 'all', label: 'Toda prioridad' },
            { value: 'high', label: 'Solo prioridad alta' },
          ]}
        />

        {canSeeCommunity && streets.length > 0 && (
          <NativeSelect
            value={f.street}
            defaultValue={EMPTY_FILTERS.street}
            onChange={(street: string) => set({ street, unit: null })}
            options={[
              { value: '', label: 'Todas las calles' },
              ...streets.map(s => ({ value: s, label: s })),
            ]}
          />
        )}

        {canSeeCommunity && (
          <UnitPicker
            communityId={communityId}
            value={f.unit}
            onChange={(unit: any) => set({ unit })}
          />
        )}
      </View>
    </View>
  );
}


// ============================================================
// Controles
// ============================================================

function Pill({ label, active, onPress }: any) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={tw.style(`px-3 py-1.5 rounded-full`, active ? 'bg-secondary' : 'bg-white border border-gray-200')}
    >
      <Text style={tw.style(`text-xs font-500`, active ? 'text-white' : 'text-gray-600')}>{label}</Text>
    </TouchableOpacity>
  );
}

/** Buscador de viviendas del lado del servidor (hasta ~10k por comunidad). */
function UnitPicker({ communityId, value, onChange }: any) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebouncedValue(query.trim(), 300);

  const unitsQuery = useQuery<any[]>({
    queryKey: ['ticket-filters.units', communityId, debounced],
    queryFn: () => api
      .get(`/communities/${communityId}/ticket-filters/units`, { params: { search: debounced } })
      .then(r => r.data),
    enabled: !!communityId && open && debounced.length > 0,
  });

  if (value) {
    return (
      <View style={tw`flex-row items-center bg-selected border border-selected-border rounded-[10px] pl-3 pr-2 py-[7px]`}>
        <Ionicons name="home-outline" size={14} color={tw.color('secondary')} />
        <Text style={tw`text-sm text-secondary ml-1.5 mr-2`}>{value.name}</Text>
        <TouchableOpacity onPress={() => onChange(null)}>
          <Ionicons name="close" size={14} color={tw.color('secondary')} />
        </TouchableOpacity>
      </View>
    );
  }

  const results = unitsQuery.data ?? [];

  return (
    <View style={tw`relative z-10`}>
      <View style={tw`flex-row items-center bg-[#F3F2F3] border border-gray-200 rounded-[10px] px-3 py-[7px] w-52`}>
        <Ionicons name="home-outline" size={14} color="#9ca3af" />
        <TextInput
          value={query}
          onChangeText={(t) => { setQuery(t); setOpen(true); }}
          onFocus={() => setOpen(true)}
          // Retraso para que el click en un resultado alcance a registrarse.
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Vivienda"
          placeholderTextColor="#9ca3af"
          style={tw`ml-2 flex-1 text-sm text-gray-700`}
        />
      </View>

      {open && debounced.length > 0 && (
        <View style={tw`absolute top-11 left-0 w-64 bg-white border border-gray-200 rounded-xl shadow-lg py-1`}>
          {unitsQuery.isFetching && results.length === 0 ? (
            <View style={tw`py-3 items-center`}><ActivityIndicator size="small" color="#6366f1" /></View>
          ) : results.length === 0 ? (
            <Text style={tw`px-3 py-2 text-xs text-gray-400`}>Sin resultados</Text>
          ) : results.map((u: any) => (
            <TouchableOpacity
              key={u.uuid}
              onPress={() => { onChange(u); setQuery(''); setOpen(false); }}
              style={tw`px-3 py-2 hover:bg-gray-50`}
            >
              <Text style={tw`text-sm text-gray-800`}>{u.name}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
}

const nativeControlStyle = {
  backgroundColor: '#F3F2F3',
  border: '1px solid #E5E7EB',
  borderRadius: 10,
  fontSize: 14,
  color: '#374151',
  outline: 'none',
  fontFamily: 'Poppins_400Regular',
};

/** Botón que abre un panel flotante (desplegable o calendario). `selected`: tiene un filtro aplicado. */
function triggerStyle(open: boolean, selected = false) {
  return {
    ...nativeControlStyle,
    // El borde completo, no solo borderColor: al quitar un longhand que pisa al
    // shorthand, React deja el borde en currentColor (gris oscuro).
    border: `1px solid ${open ? tw.color('secondary') : selected ? tw.color('selected-border') : '#E5E7EB'}`,
    // Con un valor distinto de "Todo" el campo se marca como seleccionado.
    ...(selected && { backgroundColor: tw.color('selected') }),
    cursor: 'pointer',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  };
}

const floatingPanelStyle = {
  position: 'fixed' as const,
  zIndex: 10000,
  boxSizing: 'border-box' as const,
  backgroundColor: '#FFFFFF',
  border: '1px solid #F3F4F6',
  borderRadius: 12,
  boxShadow: '0 12px 32px rgba(17, 24, 39, 0.14), 0 2px 6px rgba(17, 24, 39, 0.06)',
};

const PANEL_GAP = 6;

/**
 * Posición del panel junto al botón: abre hacia abajo; si no cabe y arriba hay más
 * espacio, hacia arriba. Con `width` se recorre a la izquierda para no salirse de la ventana.
 */
function floatingPosition(rect: DOMRect, desiredHeight: number, width?: number) {
  const below = window.innerHeight - rect.bottom - PANEL_GAP - 8;
  const above = rect.top - PANEL_GAP - 8;
  const openUp = below < desiredHeight && above > below;
  const left = width ? Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)) : rect.left;
  return {
    left,
    maxHeight: openUp ? above : below,
    ...(openUp
      ? { bottom: window.innerHeight - rect.top + PANEL_GAP }
      : { top: rect.bottom + PANEL_GAP }),
  };
}

/**
 * Cierra el panel con click afuera, scroll fuera del panel o resize. El panel vive en un
 * portal con posición fija (para que no lo recorte el overflow de paneles y modales), así
 * que si la página se mueve hay que cerrarlo en vez de dejarlo flotando fuera de lugar.
 */
function useDismiss(open: boolean, triggerRef: any, panelRef: any, onDismiss: () => void) {
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) onDismiss();
    };
    const onScroll = (e: Event) => {
      if (!panelRef.current?.contains(e.target as Node)) onDismiss();
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onDismiss);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onDismiss);
    };
  }, [open]);
}


const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const CALENDAR_WIDTH = 280;
const CALENDAR_HEIGHT = 340;

const pad2 = (n: number) => String(n).padStart(2, '0');
const toISODate = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const todayISODate = () => { const t = new Date(); return toISODate(t.getFullYear(), t.getMonth(), t.getDate()); };
const formatShortDate = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return `${d} ${MONTHS_SHORT[m - 1]} ${y}`; };

/**
 * Selector de fecha propio en lugar del <input type="date">: en Chrome el nativo solo abre
 * el calendario desde su iconito (el resto del campo edita día/mes/año) y su calendario no
 * acepta estilos. Trabaja con 'YYYY-MM-DD', igual que el input nativo.
 * Si se pasa min o max (el otro extremo del rango), se sombrean los días intermedios.
 */
function DateRangeInput({ value, onChange, min, max, placeholder }: any) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [view, setView] = useState({ y: 0, m: 0 });
  const [hovered, setHovered] = useState<string | null>(null);
  const triggerRef = useRef<any>(null);
  const panelRef = useRef<any>(null);

  const openPanel = () => {
    const [y, m] = (value || min || max || todayISODate()).split('-').map(Number);
    setView({ y, m: m - 1 });
    setRect(triggerRef.current.getBoundingClientRect());
    setOpen(true);
  };

  const close = (refocus = true) => {
    setOpen(false);
    setHovered(null);
    if (refocus) triggerRef.current?.focus();
  };

  const pick = (iso: string) => {
    if (iso !== value) onChange(iso);
    close();
  };

  useDismiss(open, triggerRef, panelRef, () => close(false));

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
      else if (e.key === 'Tab') close(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  const shiftMonth = (delta: number) => setView(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const today = todayISODate();
  const isDisabled = (iso: string) => (!!min && iso < min) || (!!max && iso > max);

  // Rango sombreado entre este valor y el otro extremo.
  const other = min || max;
  const rangeLo = value && other ? (value < other ? value : other) : null;
  const rangeHi = value && other ? (value < other ? other : value) : null;

  // Semana empieza en lunes.
  const offset = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(offset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const navButtonStyle = {
    width: 28, height: 28, borderRadius: 999, border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent',
  };
  const footerLinkStyle = {
    border: 'none', background: 'none', cursor: 'pointer', padding: '4px 6px',
    fontSize: 13, fontFamily: 'Poppins_500Medium', color: tw.color('secondary'),
  };

  return (
    <>
      {/* @ts-ignore — RN-web expone elementos DOM nativos */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close(false) : openPanel())}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          ...triggerStyle(open, !!value),
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          color: value ? '#374151' : '#9CA3AF',
        }}
      >
        <Ionicons name="calendar-outline" size={15} color={tw.color('secondary')} />
        {value ? formatShortDate(value) : placeholder}
      </button>

      {open && rect && createPortal(
        // @ts-ignore
        <div
          ref={panelRef}
          role="dialog"
          style={{
            ...floatingPanelStyle,
            ...floatingPosition(rect, CALENDAR_HEIGHT, CALENDAR_WIDTH),
            width: CALENDAR_WIDTH,
            overflowY: 'auto',
            padding: 12,
            fontFamily: 'Poppins_400Regular',
          }}
        >
          {/* Mes */}
          {/* @ts-ignore */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            {/* @ts-ignore */}
            <button type="button" onClick={() => shiftMonth(-1)} style={navButtonStyle} aria-label="Mes anterior">
              <Ionicons name="chevron-back" size={16} color={tw.color('secondary')} />
            </button>
            {/* @ts-ignore */}
            <span style={{ fontSize: 14, fontFamily: 'Poppins_600SemiBold', color: tw.color('secondary') }}>
              {MONTHS[view.m].charAt(0).toUpperCase() + MONTHS[view.m].slice(1)} {view.y}
            </span>
            {/* @ts-ignore */}
            <button type="button" onClick={() => shiftMonth(1)} style={navButtonStyle} aria-label="Mes siguiente">
              <Ionicons name="chevron-forward" size={16} color={tw.color('secondary')} />
            </button>
          </div>

          {/* Días */}
          {/* @ts-ignore */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 2 }}>
            {WEEKDAYS.map((w, i) => (
              // @ts-ignore
              <div key={`w${i}`} style={{ textAlign: 'center', fontSize: 11, color: '#9CA3AF', padding: '4px 0' }}>{w}</div>
            ))}

            {cells.map((day, i) => {
              if (day === null) return (
                // @ts-ignore
                <div key={`e${i}`} />
              );
              const iso = toISODate(view.y, view.m, day);
              const disabled = isDisabled(iso);
              const selected = iso === value;
              const inRange = !!rangeLo && iso >= rangeLo && iso <= rangeHi!;
              const isToday = iso === today;
              return (
                // @ts-ignore
                <div
                  key={iso}
                  style={{ backgroundColor: inRange && !selected ? tw.color('secondary-50') : 'transparent', display: 'flex', justifyContent: 'center' }}
                >
                  {/* @ts-ignore */}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => pick(iso)}
                    onMouseEnter={() => setHovered(iso)}
                    onMouseLeave={() => setHovered(h => (h === iso ? null : h))}
                    style={{
                      width: 34, height: 34, borderRadius: 999, cursor: disabled ? 'default' : 'pointer',
                      fontSize: 13,
                      fontFamily: selected || isToday ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
                      border: isToday && !selected ? `1px solid ${tw.color('secondary-200')}` : '1px solid transparent',
                      color: selected ? '#FFFFFF' : disabled ? '#D1D5DB' : isToday ? tw.color('secondary') : '#374151',
                      backgroundColor: selected
                        ? tw.color('secondary')
                        : hovered === iso && !disabled ? '#F3F2F3' : 'transparent',
                    }}
                  >
                    {day}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Atajos */}
          {/* @ts-ignore */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid #F3F4F6' }}>
            {/* @ts-ignore */}
            <button
              type="button"
              disabled={isDisabled(today)}
              onClick={() => pick(today)}
              style={{ ...footerLinkStyle, ...(isDisabled(today) && { color: '#D1D5DB', cursor: 'default' }) }}
            >
              Hoy
            </button>
            {value ? (
              // @ts-ignore
              <button type="button" onClick={() => pick('')} style={{ ...footerLinkStyle, color: '#6B7280' }}>
                Borrar
              </button>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}


const MENU_MAX_HEIGHT = 280;

/**
 * Desplegable propio en lugar del <select> nativo: la lista del <select> la dibuja el
 * navegador/SO y no acepta estilos. Se cierra con click afuera, Esc, Tab, scroll o resize.
 * Flechas + Enter para elegir con teclado. Con `defaultValue` (el "Todo" de un filtro), el
 * campo se marca como seleccionado cuando el valor es otro.
 */
export function NativeSelect({ value, onChange, options, defaultValue }: any) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [active, setActive] = useState(-1);
  const triggerRef = useRef<any>(null);
  const menuRef = useRef<any>(null);

  const selected = options.find((o: any) => o.value === value);

  const openMenu = () => {
    setRect(triggerRef.current.getBoundingClientRect());
    setActive(Math.max(0, options.findIndex((o: any) => o.value === value)));
    setOpen(true);
  };

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const pick = (v: string) => {
    if (v !== value) onChange(v);
    close();
  };

  useDismiss(open, triggerRef, menuRef, () => close(false));

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(options.length - 1, i + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
      else if (e.key === 'Enter') { e.preventDefault(); if (options[active]) pick(options[active].value); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
      else if (e.key === 'Tab') close(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, active, options, value]);

  // Mantener visible la opción activa al moverse con flechas.
  useEffect(() => {
    if (open) menuRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  let menuPosition: any = {};
  if (rect) {
    const position = floatingPosition(rect, Math.min(MENU_MAX_HEIGHT, options.length * 38 + 12));
    menuPosition = {
      ...position,
      minWidth: rect.width,
      maxWidth: window.innerWidth - position.left - 16,
      maxHeight: Math.min(MENU_MAX_HEIGHT, position.maxHeight),
    };
  }

  return (
    <>
      {/* @ts-ignore — RN-web expone elementos DOM nativos */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={(e: any) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { e.preventDefault(); openMenu(); }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...triggerStyle(open, defaultValue !== undefined && value !== defaultValue),
          padding: '8px 34px 8px 14px',
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23510C76' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
          backgroundSize: '12px',
        }}
      >
        {selected?.label ?? ''}
      </button>

      {open && rect && createPortal(
        // @ts-ignore
        <div
          ref={menuRef}
          role="listbox"
          style={{ ...floatingPanelStyle, ...menuPosition, overflowY: 'auto', padding: 6 }}
        >
          {options.map((opt: any, i: number) => {
            const isSelected = opt.value === value;
            return (
              // @ts-ignore
              <div
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(opt.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontSize: 14,
                  fontFamily: isSelected ? 'Poppins_500Medium' : 'Poppins_400Regular',
                  color: isSelected ? tw.color('secondary') : '#374151',
                  backgroundColor: i === active ? '#F3F2F3' : 'transparent',
                }}
              >
                {opt.label}
                {isSelected && <Ionicons name="checkmark" size={16} color={tw.color('secondary')} />}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
